import React, { type ComponentPropsWithoutRef, forwardRef, type ReactNode } from 'react';

import { type ColoredCard } from '../ColoredCard';
import { Flex } from '../Flex';
import type { IconComponent } from '../Icon/createIcon';
import {
  AlertCircleOutlinedIcon,
  BanFilledIcon,
  ClearFilledIcon,
  InfoCircleOutlinedIcon,
  ValidateCircleOutlinedIcon,
} from '../Icon/icons';
import { Text, type TextVariant } from '../Text';

import { Styled } from './styles';

type ColoredCardProps = ComponentPropsWithoutRef<typeof ColoredCard>;

export type BannerVariant = 'info' | 'warning' | 'error' | 'success';
type BannerProps = {
  variant: BannerVariant;
  onClose?: () => void;
  heading: ReactNode;
  // Bold above a body, `body-lighter` when the heading is the whole message. To change it, pass a variant
  // here rather than wrapping the heading in a Text of your own, which styles the component from the outside.
  headingVariant?: TextVariant;
  body?: ReactNode;
  footer?: ReactNode;
  // Replaces the variant icon, centered on the height of the heading and body together (an emoji, for instance).
  icon?: ReactNode;
  // On the right, centered on the height of the banner (a button opening a link, for instance).
  action?: ReactNode;
};

type ColoredCardPropsToForward = Omit<ColoredCardProps, 'color' | 'children' | keyof BannerProps>;

type Props = ColoredCardPropsToForward & BannerProps;

const coloredCardColorByVariant: Record<BannerVariant, ColoredCardProps['color']> = {
  error: 'red',
  info: 'blue',
  success: 'green',
  warning: 'orange',
};
const iconByVariant: Record<BannerVariant, IconComponent> = {
  error: BanFilledIcon,
  info: InfoCircleOutlinedIcon,
  success: ValidateCircleOutlinedIcon,
  warning: AlertCircleOutlinedIcon,
};
// Screen readers read an error out as soon as it appears; a caller passing its own `role` overrides it.
const roleByVariant: Record<BannerVariant, ColoredCardProps['role']> = {
  error: 'alert',
  info: undefined,
  success: undefined,
  warning: undefined,
};

export const Banner = forwardRef<HTMLDivElement, Props>(
  (
    {
      variant,
      heading,
      body,
      headingVariant = body ? 'body' : 'body-lighter',
      footer,
      icon,
      action,
      onClose,
      ...props
    },
    ref,
  ) => (
    <Styled.ColoredCard role={roleByVariant[variant]} {...props} color={coloredCardColorByVariant[variant]} ref={ref}>
      {onClose && (
        <Styled.CloseIconContainer aria-label="close" role="button" onClick={onClose}>
          <ClearFilledIcon />
        </Styled.CloseIconContainer>
      )}

      <Styled.FlexRow space="m" alignItems="flex-start">
        {icon ? (
          <Styled.CustomIconContainer data-testid="banner-custom-icon">{icon}</Styled.CustomIconContainer>
        ) : (
          <Styled.VariantIconContainer>{React.createElement(iconByVariant[variant])}</Styled.VariantIconContainer>
        )}

        <Flex.Column space="m">
          <Flex.Column space="xs">
            <Styled.HeadingText variant={headingVariant} closeable={!!onClose}>
              {heading}
            </Styled.HeadingText>
            <Text variant="body-lighter">{body}</Text>
          </Flex.Column>

          {footer && <div>{footer}</div>}
        </Flex.Column>

        {action && <Styled.ActionContainer closeable={!!onClose}>{action}</Styled.ActionContainer>}
      </Styled.FlexRow>
    </Styled.ColoredCard>
  ),
);
Banner.displayName = 'Banner';
